import type { ReactNode } from 'react';
import type { EmoteIconKey } from './registry';

function Frame({
  children,
  rim,
  fill,
  className = '',
}: {
  children: ReactNode;
  rim: string;
  fill: string;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      aria-hidden
      focusable="false"
    >
      <circle cx="16" cy="16" r="15" fill={fill} />
      <circle
        cx="16"
        cy="16"
        r="14.25"
        fill="none"
        stroke={rim}
        strokeWidth="1.5"
      />
      <circle
        cx="16"
        cy="16"
        r="12.5"
        fill="none"
        stroke={rim}
        strokeOpacity="0.35"
        strokeWidth="0.75"
      />
      <g fill="currentColor" stroke="currentColor">
        {children}
      </g>
      <ellipse
        cx="12"
        cy="10"
        rx="8"
        ry="4"
        fill="#ffffff"
        fillOpacity="0.08"
      />
    </svg>
  );
}

type GlyphDef = {
  rim: string;
  fill: string;
  mark: ReactNode;
};

/** Heraldic silhouettes — readable at 28–56px. */
export const EMOTE_GLYPHS: Record<EmoteIconKey, GlyphDef> = {
  victory: {
    rim: '#c9a227',
    fill: '#2a2416',
    mark: (
      <>
        <path
          fill="currentColor"
          stroke="none"
          d="M10 8.5h3.2v9.2c0 1.7 1.1 2.8 2.8 2.8s2.8-1.1 2.8-2.8V8.5H22v9.2c0 3.4-2.4 5.6-6 5.6s-6-2.2-6-5.6V8.5z"
        />
        <path fill="currentColor" stroke="none" d="M9 7h14v2.2H9z" />
      </>
    ),
  },
  defeat: {
    rim: '#c45c5c',
    fill: '#2a1717',
    mark: (
      <path
        fill="currentColor"
        stroke="none"
        d="M10.2 8.4 16 14.2l5.8-5.8 2.1 2.1L18.1 16.3l5.8 5.8-2.1 2.1L16 18.4l-5.8 5.8-2.1-2.1 5.8-5.8-5.8-5.8z"
      />
    ),
  },
  salute: {
    rim: '#3d7ea6',
    fill: '#16222c',
    mark: (
      <>
        <circle cx="16.5" cy="9.2" r="2.5" fill="currentColor" stroke="none" />
        <path
          fill="currentColor"
          stroke="none"
          d="M11.2 14c1.3-1.4 3-2.1 5.1-2.1h1.2c2.5 0 4.4 1.5 5 3.7l.3 1.2h-2.7l-.2-.9c-.3-1-1.2-1.7-2.3-1.7h-1.1c-1.3 0-2.4.5-3.1 1.4L10 20.8H7.5L11.2 14z"
        />
        <rect x="12.5" y="20.2" width="9" height="2.1" fill="currentColor" stroke="none" />
      </>
    ),
  },
  laugh: {
    rim: '#d4a24a',
    fill: '#2a2214',
    mark: (
      <>
        <circle cx="11.5" cy="13" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="20.5" cy="13" r="1.4" fill="currentColor" stroke="none" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          d="M10 17.2c1.4 2.6 3.5 3.9 6 3.9s4.6-1.3 6-3.9"
        />
      </>
    ),
  },
  glare: {
    rim: '#c45c7a',
    fill: '#27161c',
    mark: (
      <>
        <path
          fill="currentColor"
          stroke="none"
          d="M7.8 14.2c2.3-3 5-4.5 8.2-4.5s5.9 1.5 8.2 4.5c-2.3 3-5 4.5-8.2 4.5s-5.9-1.5-8.2-4.5z"
        />
        <circle cx="16" cy="14.2" r="2.3" fill="#1a1216" stroke="none" />
        <circle cx="16.7" cy="13.6" r="0.8" fill="#f2e9d8" stroke="none" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          d="M10.2 9.6 14.2 12M21.8 9.6 17.8 12"
        />
      </>
    ),
  },
  crown: {
    rim: '#c9a227',
    fill: '#2a2416',
    mark: (
      <path
        fill="currentColor"
        stroke="none"
        d="M7.5 20.8V11.8l3.3 2.5L16 8.5l5.2 5.8 3.3-2.5v8.999H7.5zm1.9-1.9h13.2v-3.4l-2.5 1.8-4.1-4.6-4.1 4.6-2.5-1.8v3.4z"
      />
    ),
  },
  bow: {
    rim: '#2a9d8f',
    fill: '#142422',
    mark: (
      <>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          d="M8.2 11.2c3.4 4.2 6.6 6.2 7.8 6.2s4.4-2 7.8-6.2"
        />
        <circle cx="16" cy="9.2" r="2.2" fill="currentColor" stroke="none" />
        <path
          fill="currentColor"
          stroke="none"
          d="M11.8 18.4h8.4v2h-2.2v3.1h-4v-3.1h-2.2z"
        />
      </>
    ),
  },
  rage: {
    rim: '#c45c5c',
    fill: '#2a1515',
    mark: (
      <>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          d="M8.8 12.2 12.2 14M23.2 12.2 19.8 14"
        />
        <circle cx="11.8" cy="15.4" r="1.35" fill="currentColor" stroke="none" />
        <circle cx="20.2" cy="15.4" r="1.35" fill="currentColor" stroke="none" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          d="M11.2 20.4c1.2-1.5 2.7-2.3 4.8-2.3s3.6.8 4.8 2.3"
        />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
          d="M14.6 8.4 16 10.3 17.4 8.4"
        />
      </>
    ),
  },
  smirk: {
    rim: '#5a9ec4',
    fill: '#15222a',
    mark: (
      <>
        <circle cx="11.5" cy="13.2" r="1.3" fill="currentColor" stroke="none" />
        <circle cx="20.5" cy="13.2" r="1.3" fill="currentColor" stroke="none" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          d="M11.2 18.6c1.2 1.3 2.7 2 4.8 2 1.3 0 2.5-.3 3.5-.85"
        />
      </>
    ),
  },
};

export function EmoteGlyph({
  iconKey,
  className = '',
}: {
  iconKey: EmoteIconKey;
  className?: string;
}) {
  const { rim, fill, mark } = EMOTE_GLYPHS[iconKey];
  return (
    <Frame rim={rim} fill={fill} className={className}>
      {mark}
    </Frame>
  );
}
